import type { RouteParamsGeneric } from "vue-router"

import type { AppPushDetailResponse, EdmDetailResponse, SmsDetailResponse } from "@/api/asset/types"
import type { AudienceInfoResponse } from "@/api/audience/types"
import type { CampaignDetailResponse } from "@/api/campaigns/types"
import type { CampaignThresholdConfigurationResponse } from "@/api/campaignThresholdConfiguration/types"
import type { ConfigurationResponse } from "@/api/configuration/types"
import type { JourneyItem } from "@/api/journeys/types"
import type { MemberTagItem } from "@/api/members/types"
import type { SendingConfigurationResponse } from "@/api/sendingConfiguration/types"
import type { TagAssignerItem, TagItem } from "@/api/tags/types"
import type { UploadFileInfoResponse } from "@/api/uploads/types"
import type { UserItem } from "@/api/users/types"

// `useRouterProtect` 在 beforeEnter 預先打 API 取得的初始 detail response，
// 借用 to.params 暫存給頁面元件用（避免頁面 mounted 時重打一次 API）。
// vue-router 的 RouteParamsGeneric 是 type 別名（不是 interface），無法用 declare module 擴充，
// 所以這裡集中定義實際會被塞進 params 的欄位，搭配 setRouteParam 收斂斷言位置。
export interface RouterPrefetchedParams {
    initialUserDataResponse?: { data: UserItem }
    campaignResponse?: { data: CampaignDetailResponse }
    initialJourneyResponse?: { data: JourneyItem }
    searchDates?: string[]
    initialAudiencesResponse?: { data: AudienceInfoResponse }
    initialTagsResponse?: { data: TagItem }
    initialTagAssignerResponse?: { data: TagAssignerItem }
    // 同一個欄位在不同路由分別存放三種不同的設定回應，只能用 union 表示
    initialConfigurationResponse?: {
        data: ConfigurationResponse | SendingConfigurationResponse | CampaignThresholdConfigurationResponse
    }
    initialEdmResponse?: { data: EdmDetailResponse }
    initialSmsResponse?: { data: SmsDetailResponse }
    initialAppPushResponse?: { data: AppPushDetailResponse }
    memberTags?: MemberTagItem[]
    initialUploadFileResponse?: { data: UploadFileInfoResponse }
}

// 把 API response 等非字串資料暫存進 to.params，供頁面元件讀取。
export function setRouteParam<K extends keyof RouterPrefetchedParams>(
    params: RouteParamsGeneric,
    key: K,
    value: RouterPrefetchedParams[K]
) {
    ;(params as unknown as RouterPrefetchedParams)[key] = value
}
